import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService, User, OriginalUserPermissions } from '../../services/user.service';
import { ClientService, Client, ClientLookupItem } from '../../services/client.service';
import { ImpersonationService, ImpersonationState } from '../../services/impersonation.service';
import { AuthService } from '../../services/auth.service';
import { AuthModalService } from '../../services/auth-modal.service';
import { ToastService } from '../../services/toast.service';
import { ImpersonationComponent } from '../../modals/impersonation/impersonation.component';
import { APP_CONFIG_TOKEN } from '../../config';
import { NotificationsComponent } from '../notifications/notifications.component';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-header',
  imports: [NotificationsComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private impersonationService = inject(ImpersonationService);
  private authService = inject(AuthService);
  private authModalService = inject(AuthModalService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private readonly config = inject(APP_CONFIG_TOKEN);

  currentUser: User | null = null;
  currentClient: Client | null = null;
  clientLookupItems: ClientLookupItem[] = [];
  isMobileMenuOpen = false;
  impersonationState: ImpersonationState = { isImpersonating: false, hasSecurityOverrides: false };
  originalUserPermissions: OriginalUserPermissions | null = null;

  ngOnInit(): void {
    this.userService.currentUser$.subscribe(user => {
      this.currentUser = user;
      // Load client lookup when user changes and is showcase user
      if (user?.isShowcaseUser) {
        this.loadClientLookup();
      }
    });

    this.clientService.currentClient$.subscribe(client => {
      this.currentClient = client;
    });

    this.impersonationService.impersonationState$.subscribe(state => {
      this.impersonationState = state;
    });

    // Load user and client data on component init
    this.userService.loadCurrentUser();
    this.clientService.loadCurrentClient();
    this.loadOriginalUserPermissions();
    this.impersonationService.loadImpersonationState();
  }

  get isAuthenticated(): boolean {
    return this.currentUser?.isAuthenticated ?? false;
  }

  get isAdmin(): boolean {
    return this.currentUser?.isAdmin ?? false;
  }

  get isShowcaseUser(): boolean {
    return this.currentUser?.isShowcaseUser ?? false;
  }

  get userName(): string {
    return this.currentUser?.name ?? '';
  }

  get logoUrl(): string {
    return this.clientService.getClientLogoUrl(this.currentClient?.id);
  }

  get logoTitle(): string {
    return `Engage${this.currentClient ? ` - ${this.currentClient.name}` : ''} v${this.config.appVersion}`;
  }

  private loadClientLookup(): void {
    this.clientService.getClientLookup().subscribe({
      next: clients => {
        this.clientLookupItems = clients;
      },
      error: error => {
        logger.error('Failed to load client lookup:', error);
        this.toastService.error('Failed to load client list');
      },
    });
  }

  private loadOriginalUserPermissions(): void {
    this.userService.getOriginalUserPermissions().subscribe({
      next: permissions => {
        this.originalUserPermissions = permissions;
      },
      error: error => {
        // Only log errors that aren't 401 Unauthorized (expected for unauthenticated users)
        if (error.status !== 401) {
          logger.error('Failed to load original user permissions:', error);
        }
        this.originalUserPermissions = null;
      },
    });
  }

  onClientChange(clientId?: number): void {
    this.clientService.changeClient(clientId).subscribe({
      next: () => {
        // Reload the page to reflect the client change
        window.location.href = '/';
      },
      error: error => {
        logger.error('Failed to change client:', error);
        this.toastService.error('Failed to change client');
      },
    });
  }

  // Mobile menu functionality
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.updateMobileMenuDisplay();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.updateMobileMenuDisplay();
  }

  private updateMobileMenuDisplay(): void {
    const overlay = document.getElementById('mobileMenuOverlay');
    if (overlay) {
      overlay.style.display = this.isMobileMenuOpen ? 'block' : 'none';
    }
  }

  // Navigation methods
  navigateToProjects(): void {
    this.closeMobileMenu();
    this.router.navigate(['/projects']);
  }

  navigateToAdmin(): void {
    this.closeMobileMenu();
    this.router.navigate(['/admin']);
  }

  onUserMenuClick(): void {
    this.closeMobileMenu();
    // Navigate to user profile management page
    this.router.navigate(['/manage']);
  }

  onLogout(): void {
    this.closeMobileMenu();
    this.authService.logout().subscribe({
      next: () => {
        // Redirect to home page after logout
        window.location.href = '/';
      },
      error: error => {
        logger.error('Logout failed:', error);
        this.toastService.error('Logout failed');
      },
    });
  }

  onLogin(): void {
    this.closeMobileMenu();
    // Open login modal using the auth modal service
    this.authModalService.openLoginModal();
  }

  // Impersonation functionality
  get canImpersonate(): boolean {
    // Use original user permissions to determine if impersonation is allowed
    return this.originalUserPermissions?.canImpersonate ?? false;
  }

  get impersonationButtonClass(): string {
    return this.impersonationState.hasSecurityOverrides ? 'btn-danger' : 'btn-light';
  }

  get impersonationButtonTitle(): string {
    return this.impersonationState.overrideDescription || 'Impersonation';
  }

  openImpersonationModal(): void {
    const modalRef = this.modalService.open(ImpersonationComponent, {
      size: 'lg',
      backdrop: 'static',
    });
  }
}
